import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Headphones, 
  Phone,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowRight,
  Shield,
  CreditCard,
  AlertCircle,
  Ear,
  Clock,
  Lock,
  Activity
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function SelfTestPage() {
  const { testCode } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testData, setTestData] = useState(null);
  
  // OTP flow state
  const [step, setStep] = useState("verify"); // verify, payment, ready, testing
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  
  // Payment state
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  
  // PTA session state
  const [ptaSessionId, setPtaSessionId] = useState(null);

  useEffect(() => {
    if (testCode) {
      fetchTestData();
    }
  }, [testCode]);

  const fetchTestData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/self-tests/code/${testCode}`);
      setTestData(response.data);
      // Don't pre-fill phone - API returns only last 4 digits for privacy
      // Patient should enter their full number for OTP verification
      
      // Determine initial step based on status
      if (response.data.status === "test_completed") {
        setStep("completed");
      } else if (response.data.status === "test_in_progress") {
        setStep("testing");
      } else if (response.data.payment_status === "completed" || !response.data.payment_required) {
        setStep("ready");
        setPaymentComplete(true);
      } else if (response.data.status === "otp_verified") {
        setOtpVerified(true);
        setStep("payment");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Test not found");
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async () => {
    setSendingOtp(true);
    try {
      const response = await axios.post(`${API_URL}/api/patient/request-otp`, {
        phone: phone,
        name: testData?.patient_name
      });
      setOtpSent(true);
      // For development, show the OTP
      if (response.data.dev_otp) {
        setDevOtp(response.data.dev_otp);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    setVerifyingOtp(true);
    try {
      const response = await axios.post(`${API_URL}/api/patient/verify-otp`, {
        phone: phone,
        otp: otp,
        session_id: testData?.id
      });
      
      // Store patient token
      localStorage.setItem("patient_token", response.data.token);
      localStorage.setItem("patient_data", JSON.stringify(response.data.patient));
      
      setOtpVerified(true);
      
      // Move to next step
      if (testData?.payment_required && testData?.payment_status !== "completed") {
        setStep("payment");
      } else {
        setStep("ready");
        setPaymentComplete(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const initiatePayment = async () => {
    setProcessingPayment(true);
    setError(null);
    
    try {
      // Create payment order
      const orderResponse = await axios.post(
        `${API_URL}/api/self-tests/${testData.id}/create-payment-order`
      );
      
      const orderData = orderResponse.data;
      
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }
      
      // Razorpay checkout
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.name,
        description: orderData.description,
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        theme: {
          color: "#4f46e5"
        },
        handler: async function (response) {
          await verifyPayment(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setError(`Payment failed: ${response.error.description}`);
        setProcessingPayment(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || "Payment initiation failed");
      setProcessingPayment(false);
    }
  };

  const verifyPayment = async (orderId, paymentId, signature) => {
    try {
      await axios.post(`${API_URL}/api/self-tests/${testData.id}/verify-payment`, null, {
        params: { order_id: orderId, payment_id: paymentId, signature: signature }
      });
      
      setPaymentComplete(true);
      setStep("ready");
      fetchTestData();
    } catch (err) {
      setError(err.response?.data?.detail || "Payment verification failed");
    } finally {
      setProcessingPayment(false);
    }
  };

  const startTest = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/self-tests/${testData.id}/start-pta`);
      setPtaSessionId(response.data.pta_session_id);
      
      // Navigate to the PTA test page
      navigate(`/pta-test/patient/${response.data.session_code}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start test");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading your hearing test...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !testData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Headphones className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">HearClear</h1>
          <p className="text-slate-600">Online Hearing Assessment</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
            step === "verify" ? "bg-indigo-600 text-white" : otpVerified ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
          }`}>
            {otpVerified ? <CheckCircle className="w-5 h-5" /> : "1"}
          </div>
          <div className={`w-12 h-1 ${otpVerified ? "bg-emerald-500" : "bg-slate-200"}`} />
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
            step === "payment" ? "bg-indigo-600 text-white" : paymentComplete ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
          }`}>
            {paymentComplete ? <CheckCircle className="w-5 h-5" /> : "2"}
          </div>
          <div className={`w-12 h-1 ${paymentComplete ? "bg-emerald-500" : "bg-slate-200"}`} />
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
            step === "ready" || step === "testing" ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"
          }`}>
            3
          </div>
        </div>

        {/* Step 1: Verify Phone */}
        {step === "verify" && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <Phone className="w-5 h-5 text-indigo-600" />
                Verify Your Phone
              </CardTitle>
              <CardDescription>
                Enter your mobile number to receive a verification code
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {testData?.patient_name && (
                <div className="p-3 bg-indigo-50 rounded-lg text-center">
                  <p className="text-sm text-indigo-600">Welcome, <strong>{testData.patient_name}</strong></p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={testData?.patient_phone ? `Enter number ending in ${testData.patient_phone}` : "+91 98765 43210"}
                  disabled={otpSent}
                  data-testid="phone-input"
                />
              </div>

              {!otpSent ? (
                <Button 
                  onClick={requestOtp} 
                  disabled={!phone || sendingOtp}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="send-otp-btn"
                >
                  {sendingOtp ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Send OTP
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Enter OTP</Label>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 4-digit OTP"
                      maxLength={4}
                      className="text-center text-2xl tracking-widest"
                    />
                    {devOtp && (
                      <p className="text-xs text-amber-600 text-center">
                        [Dev Mode] Your OTP is: <strong>{devOtp}</strong>
                      </p>
                    )}
                  </div>
                  
                  <Button 
                    onClick={verifyOtp}
                    disabled={otp.length !== 4 || verifyingOtp}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="verify-otp-btn"
                  >
                    {verifyingOtp ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Verify OTP
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp("");
                    }}
                  >
                    Resend OTP
                  </Button>
                </div>
              )}

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Payment */}
        {step === "payment" && testData?.payment_required && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                Complete Payment
              </CardTitle>
              <CardDescription>
                Pay ₹{(testData.payment_amount / 100).toFixed(0)} to proceed with your hearing assessment
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Online Hearing Assessment</span>
                  <span className="font-bold text-slate-900">₹{(testData.payment_amount / 100).toFixed(0)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-500">
                  <span>Includes comprehensive PTA test + AI recommendations</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>6-frequency hearing threshold test</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Instant audiogram & severity classification</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>AI-powered recommendations</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Downloadable PDF report</span>
                </div>
              </div>

              <Button 
                onClick={initiatePayment}
                disabled={processingPayment}
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
                data-testid="pay-now-btn"
              >
                {processingPayment ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Pay ₹{(testData.payment_amount / 100).toFixed(0)} Securely
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                <Shield className="w-4 h-4" />
                <span>Secured by Razorpay</span>
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Ready to Start */}
        {step === "ready" && (
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Ear className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle>Ready to Start!</CardTitle>
              <CardDescription>
                Your hearing assessment is ready. Please follow these instructions:
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Headphones className="w-4 h-4" />
                  Before You Begin
                </h4>
                <ul className="space-y-2 text-sm text-amber-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Use headphones or earphones for accurate results</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Find a quiet environment with minimal background noise</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Set your device volume to about 50-70%</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>The test takes approximately 5-8 minutes</span>
                  </li>
                </ul>
              </div>

              <Button 
                onClick={startTest}
                className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 text-lg"
                data-testid="start-hearing-test-btn"
              >
                <Headphones className="w-5 h-5 mr-2" />
                Start Hearing Test
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step: Test In Progress */}
        {step === "testing" && (
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Activity className="w-8 h-8 text-indigo-600 animate-pulse" />
              </div>
              <CardTitle>Test In Progress</CardTitle>
              <CardDescription>
                Your hearing test has already been started.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                <p className="text-sm text-indigo-700">
                  If you were disconnected, you can resume your test by clicking below.
                </p>
              </div>
              {testData?.pta_session_id ? (
                <Button 
                  onClick={() => {
                    // Fetch PTA session to get session code
                    axios.get(`${API_URL}/api/pta-live-sessions/${testData.pta_session_id}`).then(res => {
                      navigate(`/pta-test/patient/${res.data.session_code}`);
                    }).catch(() => {
                      setError("Unable to resume test. Please contact support.");
                    });
                  }}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 text-lg"
                  data-testid="resume-test-btn"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Resume Hearing Test
                </Button>
              ) : (
                <p className="text-sm text-slate-500 text-center">Please contact support if you need assistance.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step: Test Completed */}
        {step === "completed" && (
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-emerald-700">Test Completed!</CardTitle>
              <CardDescription>
                Your hearing assessment has been completed successfully.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-700 text-center">
                  Your results have been recorded. A HearClear audiologist will review your results and contact you with recommendations.
                </p>
              </div>
              <div className="text-center text-sm text-slate-500">
                <p>Thank you for taking the HearClear hearing assessment.</p>
                <p className="mt-1">We'll be in touch shortly with your personalized report.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>© 2024 HearClear. All rights reserved.</p>
          <p className="mt-1">Questions? Call us at 1800-XXX-XXXX</p>
        </div>
      </div>
    </div>
  );
}
